import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSearch, FaSyncAlt, FaCommentDots, FaCalendarAlt, FaEnvelope, FaUser, FaGlobe, FaClock, FaRobot, FaHashtag } from "react-icons/fa";
import "./Inbox.css";

const fmtTime = (d) => {
  const dt = new Date(d);
  return isNaN(dt) ? "—" : dt.toLocaleString();
};

const avatar = (name = "") =>
  name === "Unknown User"
    ? "?"
    : name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

const avatarColor = (name = "") => {
  const colors = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const Inbox = ({ apiBase, email, botName }) => {
  const [users, setUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [activeTab, setActiveTab] = useState("chats");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState(null);
  const [chatInfo, setChatInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const base = useMemo(() => (apiBase || "").replace(/\/$/, ""), [apiBase]);
  const loadFormFields = useCallback(async () => {
    if (!email || !botName || !base) return;

    try {
      const res = await fetch(`${base}/api/inbox/form-fields?email=${encodeURIComponent(email)}&botName=${encodeURIComponent(botName)}`);
      const data = await res.json();
      setFormFields(data.fields || []);
    } catch {
      setFormFields([]);
    }
  }, [base, email, botName]);

  const loadUsers = useCallback(async () => {
    if (!email || !botName || !base) return;
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${base}/api/inbox/users?email=${encodeURIComponent(email)}&botName=${encodeURIComponent(botName)}&limit=100`);
      const data = await res.json();
      setUsers(data.items || []);
    } catch {
      setErr("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [base, email, botName]);

  const loadAllUsers = useCallback(async () => {
    if (!email || !botName || !base) return;
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${base}/api/inbox/all-users?email=${encodeURIComponent(email)}&botName=${encodeURIComponent(botName)}`);
      const data = await res.json();
      setUsersList(data.items || []);
    } catch {
      setErr("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [base, email, botName]);

  const loadChat = useCallback(async (chatId) => {
    if (!chatId) return;
    setSelectedChatId(chatId);
    setChatLoading(true);
    setErr("");
    try {
      const res = await fetch(`${base}/api/inbox/chats/${chatId}`);
      const data = await res.json();
      setChatInfo(data.info || null);
      setMessages(data.messages || []);
    } catch {
      setErr("Failed to load chat");
    } finally {
      setChatLoading(false);
    }
  }, [base]);


  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const dataSource = activeTab === "chats" ? users : usersList;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return dataSource.filter((r) =>
      (r.user?.name || "").toLowerCase().includes(q) ||
      (r.user?.email || "").toLowerCase().includes(q) ||
      (r.ip || "").toLowerCase().includes(q) ||
      (r._id || "").toLowerCase().includes(q)
    );
  }, [dataSource, search]);

  const selectedUser = useMemo(() => {
    if (activeTab === "chats") {
      return users.find(u => u._id === selectedChatId);
    }
    return usersList.find(u => u.user?.email === selectedUserEmail);
  }, [activeTab, users, usersList, selectedChatId, selectedUserEmail]);
  const chatName = useMemo(() => {
    return (
      chatInfo?.user?.name ||
      selectedUser?.user?.name ||
      "Unknown User"
    );
  }, [chatInfo, selectedUser]);

  return (
    <div className="inbox-root">

      <aside className="inbox-sidebar">
        <div className="tabs">
          <button
            className={activeTab === "chats" ? "tab active" : "tab"}
            onClick={() => {
              setActiveTab("chats"); setSelectedChatId(null); setSelectedUserEmail(null); setSelectedUserData(null); loadUsers();
            }}
          >
            Chats
          </button>
          <button
            className={activeTab === "users" ? "tab active" : "tab"}
            onClick={() => {
              setActiveTab("users"); setSelectedChatId(null); setSelectedUserEmail(null); setSelectedUserData(null); loadAllUsers(); loadFormFields();
            }}
          >
            Users
          </button>
        </div>

        <div className="sidebar-toolbar">
          <div className="toolbar-row">
            <div className="search-wrap">
              <FaSearch className="search-icon" />
              <input
                className="search-input"
                placeholder="Search name, email, IP, chatId"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="toolbar-row">
            <button
              className="btn-refresh"
              onClick={() => {
                if (activeTab === "chats") {
                  loadUsers();
                } else {
                  loadAllUsers();
                  loadFormFields();
                }
              }}
            >
              <FaSyncAlt /> <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="sidebar-list">
          {loading && <p className="state-msg">Loading…</p>}
          {err && <p className="state-msg err">{err}</p>}
          {!loading && !err && filtered.length === 0 && (
            <p className="state-msg">No conversations found</p>
          )}

          {filtered.map((row) => {
            const name = row.user?.name || "Unknown User";
            const isActive = activeTab === "chats" ? selectedChatId === row._id : selectedUserEmail === row.user?.email;
            return (
              <div
                key={row._id || row.user?.email}
                className={`user-card ${isActive ? "active" : ""}`}
                onClick={() => {
                  if (activeTab === "chats") {
                    setSelectedUserEmail(null);
                    setSelectedUserData(null);
                    loadChat(row._id);
                  } else {
                    setSelectedChatId(null);
                    setSelectedUserEmail(row.user?.email);
                    setSelectedUserData(row);
                  }
                }}
              >
                <div className="uc-avatar" style={{ background: avatarColor(name) }}>
                  {avatar(name)}
                </div>

                <div className="uc-body">
                  <div className="uc-name">{name}</div>

                  <div className="uc-row">
                    <FaEnvelope />
                    <span>{row.user?.email || "—"}</span>
                    {row.ip && (
                      <>
                        <span className="dot">•</span>
                        <span>{row.ip}</span>
                      </>
                    )}
                  </div>

                  {row.lastAt && (
                    <div className="uc-row">
                      <FaCalendarAlt />
                      <span>Last: {fmtTime(row.lastAt)}</span>
                    </div>
                  )}

                  {row.count !== undefined && (
                    <div className="uc-row">
                      <FaCommentDots />
                      <span>Messages: {row.count}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      <section className="inbox-chat">
        {!(selectedChatId || selectedUserEmail) ? (
          <div className="empty-state">
            <FaCommentDots />
            <p>Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            <div className="chat-topbar">
              <div
                className="ct-avatar"
                style={{ background: avatarColor(chatName) }}
              >
                {avatar(chatName)}
              </div>

              <div>
                <div className="ct-name">{chatName}</div>
                {chatInfo && (
                  <div className="ct-sub">
                    <span className="online-dot" />
                    {fmtTime(chatInfo.lastAt)}
                  </div>
                )}
              </div>
            </div>

            <div className="chat-scroll">

              {activeTab === "users" && !selectedChatId && selectedUserData && (
                <div className="user-details-wrapper">

                  <div className="info-list">
                    <InfoRow icon={<FaUser />} label="Name" value={selectedUserData.user?.name || "Unknown"} />
                    <InfoRow icon={<FaEnvelope />} label="Email" value={selectedUserData.user?.email || "—"} />
                    <InfoRow icon={<FaGlobe />} label="IP address" value={selectedUserData.ip || "—"} />
                    <InfoRow icon={<FaCommentDots />} label="Messages" value={selectedUserData.count} />
                  </div>

                  {formFields.length > 0 && (
                    <>
                      <h4 className="section-title">Form Details</h4>

                      <div className="info-list">
                        {formFields.map((field, i) => (
                          <InfoRow key={i} icon={<FaUser />} label={field.label} value={selectedUserData.user?.[field.name] || "—"} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {selectedChatId && messages.map((m, i) => (
                <div key={i} className="chat-block">
                  {m.question && (
                    <div className="bubble-row user-row">
                      <div className="bubble bubble-user">{m.question}</div>
                      <span className="bubble-ts">{fmtTime(m.ts)}</span>
                    </div>
                  )}

                  {m.answer && (
                    <div className="bubble-row bot-row">
                      <div className="bubble bubble-bot">{m.answer}</div>
                    </div>
                  )}
                </div>
              ))}
              {activeTab === "users" && selectedChatId && (
                <button onClick={() => setSelectedChatId(null)}>
                  ← Back to chats
                </button>
              )}
            </div>
          </>
        )}
      </section>

      <aside className="inbox-info">
        <h4 className="info-title">User info</h4>

        {!selectedChatId ? (
          <p className="state-msg">No session selected</p>
        ) : !chatInfo ? (
          <p className="state-msg">Loading chat info...</p>
        ) : (
          <div className="info-list">
            <InfoRow icon={<FaHashtag />} label="Chat ID" value={chatInfo?.chat_id || selectedChatId || "—"} />
            <InfoRow icon={<FaUser />} label="Name" value={chatInfo.user?.name || "Unknown"} />
            <InfoRow icon={<FaEnvelope />} label="Email" value={chatInfo.user?.email || "—"} />
            <InfoRow icon={<FaGlobe />} label="IP address" value={chatInfo.ip || "—"} />
            <InfoRow icon={<FaClock />} label="Last interaction" value={
              <>
                {fmtTime(chatInfo.lastAt)}
                {chatInfo.botName && (
                  <span className="bot-badge">
                    <FaRobot /> {chatInfo.botName}
                  </span>
                )}
              </>
            } />
          </div>
        )}
      </aside>
    </div>
  );
};

const InfoRow = ({ icon, label, value }) => (
  <div className="info-row">
    <div className="info-icon">{icon}</div>
    <div className="info-content">
      <span className="info-label">{label}</span>
      <span className="info-value">{value}</span>
    </div>
  </div>
);

export default Inbox;
